import React, { useMemo } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography
} from '@mui/material';
import { Edit, Delete, HighlightOffOutlined, CheckCircleOutlined, HourglassTop } from '@mui/icons-material';
import { CustomTooltip } from 'components/CustomTooltip/CustomTooltip';
import { TrimCharacters } from 'commonFunctions/commonFunctions';
import * as c from "utils/constants/constants";

const mainTextStyle = {
  fontSize: "0.9rem",
}
const subTextStyle = {
  fontSize: "0.8rem",
  color: "rgb(108, 108, 108)"
}

const tableCellStyle = {
  padding: "5px"
}
const tableHeadStyle = {
  padding: "10px 5px",
  background: "#f2f2f2"
}

const iconButtonStyle = {
  padding: "2px 0px"
}

const headerStyle = {
  color: "var(--hti-magenta)",
  fontSize: "13px",
  fontWeight: 500,
  marginBottom: "10px",
  textAlign: 'start'
}

const statusColors = { 
  [c.status.queued]:"#AA1572",
  [c.status.in_progress]:"#5d1052",
  [c.status.intervention_rqd]:"#f2994a",
  [c.status.failed]:"#EB5757",
  [c.status.processed]:"#27AE60",
  [c.status.completing]:"#b4abb2",
  [c.status.uploading]:"#4F4F4F",
  [c.status.uploaded]:"#9B51E0",
  [c.status.completed]:"#2D9CDB",
  }

const DocumentSplitTable = ({ data = [], onClick, navigateToChildDocument, documentData={}}) => {

  const enableFileNameColumn = useMemo(() => data.some(m => m.file_name || m.doc_id || m.split_error_message), [data]);
  const enableStatusColumn = useMemo(() => data.some(m => m.document_status == "Pending" || m.is_split_completed), [data]);
  return (
    <div className="doc-split-table-container">
      <Typography sx={{ ...headerStyle }}>Processed Documents</Typography>
      <div className="doc-split-table">
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead className="thBorder">
              <TableRow>
                <TableCell className="tableHeadStyle" sx={tableHeadStyle}>Patient Name</TableCell>
                {enableFileNameColumn ? <TableCell className="tableHeadStyle" sx={tableHeadStyle}>File Name</TableCell> : null}
                <TableCell className="tableHeadStyle" sx={tableHeadStyle}>Category</TableCell>
                <TableCell className="tableHeadStyle" sx={tableHeadStyle}>Range</TableCell>
                {documentData.Entity_Details.enable_doc_split == false && enableStatusColumn ? <TableCell className="tableHeadStyle" sx={{ ...tableHeadStyle, textAlign: "center" }}>Split Status</TableCell> : null}
                {documentData.Entity_Details.enable_doc_split == false && enableFileNameColumn ? <TableCell className="tableHeadStyle" sx={{ ...tableHeadStyle, textAlign: "left", width: "165px" }}>Doc Status</TableCell> : null}
                {!(documentData.Entity_Details.enable_doc_split == false) && !enableFileNameColumn  ? <TableCell className="tableHeadStyle" sx={tableHeadStyle} align='center'>Action</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {(data || []).map((row) => (
                <TableRow
                  key={row.name}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell className="tableCellStyle" component="th" scope="row" sx={tableCellStyle}>
                    <div className="cell-container">
                      <CustomTooltip title={TrimCharacters(row.patient_name, 10) != row.patient_name ? row.file_name : null}  placement="top-start">
                        <Typography sx={mainTextStyle}>{TrimCharacters(row.patient_name, 10)}</Typography>
                      </CustomTooltip>
                      <Typography sx={subTextStyle}>{row.dob}</Typography>
                    </div>
                  </TableCell>
                  {enableFileNameColumn ? <TableCell className="tableCellStyle" sx={{...tableCellStyle, pointerEvents: "auto"}}>
                    <div className="cell-container">
                      <CustomTooltip title={TrimCharacters(row.file_name, 10) != row.file_name ? row.file_name : null} placement="top-start">
                        <Typography onClick={e => navigateToChildDocument(row.doc_id)} sx={{ ...mainTextStyle, ":hover": { cursor: "pointer", textDecoration: "underline" } }}>{row.file_name ? TrimCharacters(row.file_name, 10) : "-"}</Typography>
                      </CustomTooltip>
                      <Typography sx={subTextStyle}>{row.doc_id}</Typography>
                    </div>
                  </TableCell> : null}
                  <TableCell className="tableCellStyle" sx={tableCellStyle}>
                    <div className="cell-container">
                      <CustomTooltip title={TrimCharacters(row.category, 10) != row.category ? row.category : null} placement="top-start">
                        <Typography sx={mainTextStyle}>{TrimCharacters(row.category, 10)}</Typography>
                      </CustomTooltip>
                      <CustomTooltip title={TrimCharacters(row.sub_category, 10) != row.sub_category ? row.sub_category : null} placement="top-start">
                        <Typography sx={subTextStyle}>{TrimCharacters(row.sub_category, 10)}</Typography>
                      </CustomTooltip>
                    </div>
                  </TableCell>
                  <TableCell className="tableCellStyle" sx={tableCellStyle}>
                    <div className="cell-container">
                      <Typography sx={mainTextStyle}>Start: {row.start}</Typography>
                      <Typography sx={mainTextStyle}>End: {row.end}</Typography>
                    </div>
                  </TableCell>
                  {documentData.Entity_Details.enable_doc_split == false && enableStatusColumn ? <TableCell className="tableCellStyle" sx={{ ...tableCellStyle, textAlign: "center" }}>
                    <div className="cell-container">
                      {row.split_error_message ? <CustomTooltip title={row.split_error_message} placement="top-start">
                        <HighlightOffOutlined sx={{ color: "#EB5757" }} fontSize="small" style={{pointerEvents: "auto", cursor: "pointer"}} />
                      </CustomTooltip> : (row.file_name || row.doc_id ? <CheckCircleOutlined sx={{ color: "#27AE60" }} fontSize="small" /> : <HourglassTop sx={{ color: "#2d9cdb" }}/>)}
                    </div>
                  </TableCell> : null}
                  {documentData.Entity_Details.enable_doc_split == false && enableFileNameColumn ? <TableCell className="tableCellStyle" sx={{ ...tableCellStyle, textAlign: "left", width: "165px" }}>
                      <div
                        className="cell-container"
                        style={{
                          color: statusColors[row.document_status] || "#828282"
                        }}
                      >
                        <Typography sx={mainTextStyle}>{row.document_status}</Typography>
                      </div>
                  </TableCell> : null}
                  {!(documentData.Entity_Details.enable_doc_split == false) && !enableFileNameColumn  ? <TableCell className="tableCellStyle" sx={tableCellStyle} align='center'>
                    <div className="cell-container" style={{pointerEvents: row.doc_id ? "none" : "auto"}}>
                      <IconButton size="large" sx={iconButtonStyle} onClick={e => typeof onClick == "function" && onClick(row, "edit")}>
                        <Edit className='icon-review-style' />
                      </IconButton>
                      <IconButton size="large" sx={iconButtonStyle} onClick={e => typeof onClick == "function" && onClick(row, "delete")}>
                        <Delete className='icon-review-style' />
                      </IconButton>
                    </div>
                  </TableCell> : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default DocumentSplitTable;
